/* from this chat https://chatgpt.com/share/6766d6f7-43c8-8013-97c0-e862bca7da9d */

interface Point {
  x: number
  y: number
}

interface CubicSegment {
  xMin: number // x_i
  xMax: number // x_{i+1}
  a: number // coefficient a_i
  b: number // coefficient b_i
  c: number // coefficient c_i
  d: number // coefficient d_i
}

function buildMonotoneCubicSegments(points: Point[]): CubicSegment[] {
  const tangents = computeTangents(points)
  const segments: CubicSegment[] = []

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i]!
    const p1 = points[i + 1]!
    const dx = p1!.x - p0!.x
    const dy = p1!.y - p0!.y

    const T0 = tangents[i]! * dx // slope at p0, scaled by dx
    const T1 = tangents[i + 1]! * dx // slope at p1, scaled by dx

    // Hermite cubic spline coefficients
    const a = p0.y
    const b = T0
    const c = 3 * dy - 2 * T0 - T1
    const d = -2 * dy + T0 + T1

    segments.push({xMin: p0.x, xMax: p1.x, a, b, c, d})
  }
  return segments
}

// Helper to get an array of “raw” slopes between consecutive points
function computeSlopes(points: {x: number; y: number}[]): number[] {
  const slopes: number[] = []
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1]!.x - points[i]!.x
    const dy = points[i + 1]!.y - points[i]!.y
    slopes.push(dx == 0 ? 1e10 : dy / dx) // Use a very large number to represent a vertical line
  }
  return slopes
}

/**
 * Compute tangents T_i at each point i such that the spline
 * remains monotonic. This matches the approach used in
 * "Monotone Cubic Interpolation".
 */
function computeTangents(points: {x: number; y: number}[]): number[] {
  const n = points.length
  const slopes = computeSlopes(points)

  // Tangents (we'll store one for each point)
  const tangents = new Array(n).fill(0)

  // The endpoints get special treatment:
  //  - T_0 is scaled version of slope[0]
  //  - T_(n-1) is scaled version of slope[n-2]
  tangents[0] = slopes[0]
  tangents[n - 1] = slopes[n - 2]

  // For interior points, average slopes
  for (let i = 1; i < n - 1; i++) {
    // average slope around i
    const s1 = slopes[i - 1]!
    const s2 = slopes[i]!
    // If s1 and s2 have the same sign, average them
    if (s1 * s2 > 0) {
      tangents[i] = (s1 + s2) / 2
    } else {
      // If slopes have different signs, the tangent is zero
      tangents[i] = 0
    }
  }

  // Now we enforce monotonicity:
  // If we find that the product of tangent i and slope i is larger than slope^2,
  // we need to “shrink” the tangent.
  for (let i = 0; i < n - 1; i++) {
    if (slopes[i] === 0) {
      tangents[i] = 0
      tangents[i + 1] = 0
    } else {
      const alpha = tangents[i] / slopes[i]!
      const beta = tangents[i + 1] / slopes[i]!
      const sum = alpha ** 2 + beta ** 2

      if (sum > 9) {
        const tau = 3 / Math.sqrt(sum)
        tangents[i] = tau * alpha * slopes[i]!
        tangents[i + 1] = tau * beta * slopes[i]!
      }
    }
  }

  return tangents
}

function evaluateCubic(segment: CubicSegment, x: number): number {
  const {xMin, xMax, a, b, c, d} = segment
  const range = xMax - xMin
  // Guard for case range=0, though it shouldn't happen if x's are distinct
  if (range === 0) return a

  const t = (x - xMin) / range
  return a + b * t + c * t * t + d * t * t * t
}

export function createMonotoneInterpolator(points: Point[]): {getY: (x: number) => number | undefined} {
  const sorted = [...points].sort((a, b) => a.x - b.x)
  const segments = buildMonotoneCubicSegments(sorted)

  // Return an object that has getY
  function getY(x: number): number | undefined {
    if (sorted.length == 0 || x <= sorted[0]!.x) return undefined // If x is out of range to the left
    if (x >= sorted[sorted.length - 1]!.x) return undefined // If x is out of range to the right
    // Otherwise, find the segment that contains x
    for (const seg of segments) if (x >= seg.xMin && x <= seg.xMax) return evaluateCubic(seg, x)
    return undefined
  }
  return {getY}
}
